import { useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  CircularProgress,
  Box,
  FormControlLabel,
  Checkbox,
} from '@mui/material';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import { useTranslate } from 'react-admin';

interface DeleteMessageConfirmDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (hardDelete: boolean) => Promise<boolean>;
  /** Remaining time to delete (e.g., "5 min") or null if no limit */
  remainingTime?: string | null;
  /** Whether to show hard delete option */
  showHardDeleteOption?: boolean;
}

/**
 * Confirmation dialog for deleting a message.
 * Optionally shows a "hard delete" checkbox to permanently remove message content.
 */
export function DeleteMessageConfirmDialog({
  open,
  onClose,
  onConfirm,
  remainingTime,
  showHardDeleteOption = true,
}: DeleteMessageConfirmDialogProps) {
  const translate = useTranslate();
  const [isLoading, setIsLoading] = useState(false);
  const [hardDelete, setHardDelete] = useState(false);

  const handleConfirm = async () => {
    setIsLoading(true);
    const success = await onConfirm(hardDelete);
    setIsLoading(false);

    if (success) {
      setHardDelete(false); // Reset for next time
      onClose();
    }
  };

  const handleClose = () => {
    if (!isLoading) {
      setHardDelete(false);
      onClose();
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
    >
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <WarningAmberIcon color="warning" />
        {translate('resources.message.dialogs.deleteTitle')}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="textSecondary" gutterBottom>
          {translate('resources.message.dialogs.deleteConfirmation')}
        </Typography>

        {remainingTime && (
          <Box mt={1}>
            <Typography variant="caption" color="warning.main">
              {translate('resources.message.dialogs.deleteTimeRemaining', { time: remainingTime })}
            </Typography>
          </Box>
        )}

        {showHardDeleteOption && (
          <Box mt={2}>
            <FormControlLabel
              control={
                <Checkbox
                  checked={hardDelete}
                  onChange={(e) => setHardDelete(e.target.checked)}
                  disabled={isLoading}
                  size="small"
                />
              }
              label={
                <Typography variant="body2">
                  {translate('resources.message.dialogs.hardDeleteLabel')}
                </Typography>
              }
            />
            {hardDelete && (
              <Typography variant="caption" color="textSecondary" sx={{ display: 'block', ml: 4 }}>
                {translate('resources.message.dialogs.hardDeleteHint')}
              </Typography>
            )}
          </Box>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={isLoading}>
          {translate('ra.action.cancel')}
        </Button>
        <Button
          onClick={handleConfirm}
          variant="contained"
          color="error"
          disabled={isLoading}
          startIcon={isLoading ? <CircularProgress size={16} /> : undefined}
        >
          {translate('ra.action.delete')}
        </Button>
      </DialogActions>
    </Dialog>
  );
}
